import React, { useCallback, useEffect, useState } from 'react';
import { getPublicStatusPage } from '../utils/API';
import Monitor from './Monitor';
import MonitorSummary from './MonitorSummary';
import TimespanSelector from './TimespanSelector';
import ErrorPage from './ErrorPage';
//import Footer from './Footer';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import ExternalStatusSection from './ExternalStatusSection';
import useLanguageCode from '../hooks/useLanguageCode';
import { Config } from '../utils/Config';

function LoadingSpinner() {
  return (
    <div className="flex justify-center items-center py-32">
      <div className="relative">
        <div className="h-12 w-12 rounded-full border-[3px] border-gray-200" />
        <div className="absolute top-0 left-0 h-12 w-12 rounded-full border-[3px] border-transparent border-t-primary animate-spin" />
      </div>
    </div>
  );
}

function RefreshIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

export default function StatusPage() {
  const { t } = useTranslation();
  const languageCode = useLanguageCode();

  const domainName = Config.domain;

  const [statusPage, setStatusPage] = useState();
  const [isRefreshing, setIsRefreshing] = useState(true);
  const [error, setError] = useState();
  const [timespan, setTimespan] = useState();

  const refreshHook = useCallback(() => {
    setIsRefreshing(true);
    getPublicStatusPage(domainName.toLowerCase())
      .then(({ statusPage }) => {
        setStatusPage(statusPage);
      })
      .catch(error => {
        setError(error);
      })
      .finally(() => setIsRefreshing(false));
  }, [domainName]);

  useEffect(() => {
    refreshHook();
  }, [refreshHook]);

  useEffect(() => {
    setStatusPage(p => p && ({...p}));
  }, [languageCode]);

  useEffect(() => {
    if (statusPage) {
      document.title = statusPage.statusPageMeta.title;
    }
  }, [statusPage]);

  if (!statusPage) {
    return error ? <ErrorPage error={error} /> : <LoadingSpinner />;
  }

  const meta = statusPage.statusPageMeta;
  const monitors = statusPage.statusPageMonitors || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{meta.title}</h1>
        </div>
        <LanguageSelector />
      </div>

      {/* Monitor Summaries */}
      <div className="space-y-3 mb-10">
        {monitors.map((monitor, index) => (
          <MonitorSummary key={index} monitor={monitor} timespan={timespan} />
        ))}
      </div>

      {/* External Sources */}
      <ExternalStatusSection />

      {/* Metrics Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <TimespanSelector onChange={x => setTimespan(x)} />
        <button
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg
            text-neutral hover:bg-gray-100 hover:text-black
            disabled:opacity-40 disabled:cursor-not-allowed
            transition-colors duration-150"
          disabled={isRefreshing}
          onClick={refreshHook}
        >
          <span className={isRefreshing ? 'animate-spin' : ''}>
            <RefreshIcon />
          </span>
          {t('common.refresh')}
        </button>
      </div>

      {/* Monitor Details */}
      <div className="space-y-5">
        {monitors.map((monitor, index) => (
          <Monitor key={index} monitor={{...monitor, index}} timespan={timespan} />
        ))}
      </div>

      {/*<Footer />*/}
    </div>
  );
}
